import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { DesignerLayout } from "@/components/layout/DesignerLayout";
import { useAuth } from "@/lib/auth-context";
import { api } from "@/lib/api";
import { ArrowUpRight, FolderOpen, Clock } from "@phosphor-icons/react";

const STATUS_LABEL = {
  submitted: "Submitted",
  preproduction: "Preproduction",
  design: "Design",
  review: "Review",
  final: "Final",
  delivered: "Delivered",
};

export default function DesignerDashboard() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [p, s] = await Promise.all([api.get("/projects"), api.get("/designer/stats")]);
        setProjects(p.data);
        setStats(s.data);
      } catch (_) {}
    })();
  }, []);

  const active = projects.filter((p) => p.status !== "delivered");

  return (
    <DesignerLayout>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-10">
        <div className="lg:col-span-7">
          <div className="text-[10px] uppercase tracking-[0.4em] text-[#FF2D2D]">Designer</div>
          <h1 className="font-display text-5xl md:text-6xl mt-2" data-testid="designer-greeting">
            Hey, {user?.name?.split(" ")[0]}.
          </h1>
          <p className="text-[#a3a3a3] mt-3 max-w-xl">Your assigned work, all in one place.</p>
        </div>
        <div className="lg:col-span-5 grid grid-cols-2 gap-px bg-[#262626] border border-[#262626]">
          <div className="bg-[#0a0a0a] p-3 md:p-5 min-w-0">
            <FolderOpen size={18} className="text-[#FF2D2D]" />
            <div className="font-display text-xl md:text-2xl lg:text-3xl mt-2 tabular-nums truncate" data-testid="d-stat-active">
              {stats?.active ?? "—"}
            </div>
            <div className="text-[10px] md:text-xs text-[#a3a3a3] uppercase tracking-wider">Active jobs</div>
          </div>
          <div className="bg-[#0a0a0a] p-3 md:p-5 min-w-0">
            <Clock size={18} className="text-[#FF2D2D]" />
            <div className="font-display text-xl md:text-2xl lg:text-3xl mt-2 tabular-nums truncate" data-testid="d-stat-total">
              {stats?.total ?? "—"}
            </div>
            <div className="text-[10px] md:text-xs text-[#a3a3a3] uppercase tracking-wider">All time</div>
          </div>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-2xl uppercase">Assigned to you</h2>
          <span className="text-xs text-[#a3a3a3]">{active.length} active · {projects.length} total</span>
        </div>
        {projects.length === 0 ? (
          <div className="border border-[#262626] bg-[#0a0a0a] p-10 text-center text-[#a3a3a3]" data-testid="designer-no-projects">
            No projects assigned to you yet.
          </div>
        ) : (
          <div className="border border-[#262626] divide-y divide-[#262626]" data-testid="designer-projects">
            {projects.map((p) => (
              <Link
                key={p.id}
                to={`/designer/projects/${p.id}`}
                data-testid={`designer-project-${p.id}`}
                className="grid grid-cols-12 gap-4 p-4 bg-[#0a0a0a] hover:bg-[#121212] transition-colors items-center"
              >
                <div className="col-span-12 md:col-span-5">
                  <div className="font-display text-xl">{p.title}</div>
                  <div className="text-xs text-[#a3a3a3] uppercase tracking-wider mt-1">{p.service_type} · {p.client_name}</div>
                </div>
                <div className="col-span-6 md:col-span-3">
                  <div className="text-xs text-[#a3a3a3]">Status</div>
                  <div className="text-sm font-semibold text-[#FF2D2D]">{STATUS_LABEL[p.status] || p.status}</div>
                </div>
                <div className="col-span-6 md:col-span-3">
                  <div className="text-xs text-[#a3a3a3]">Turnaround</div>
                  <div className="text-sm font-semibold capitalize">{p.turnaround?.replace("_", " ")}</div>
                </div>
                <div className="col-span-12 md:col-span-1 flex justify-end">
                  <ArrowUpRight size={20} className="text-[#a3a3a3]" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </DesignerLayout>
  );
}
